import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  class: "w-full flex flex-col gap-6 text-left",
  "data-testid": "check-validation-panel"
}
const _hoisted_2 = {
  key: 0,
  class: "w-full flex flex-col gap-3 rounded-lg border border-warning bg-warning/10 p-4 sm:flex-row sm:items-center sm:justify-between",
  role: "status",
  "data-testid": "check-validation-retired-banner"
}
const _hoisted_3 = { class: "flex items-start gap-3" }
const _hoisted_4 = {
  class: "w-5 h-5 shrink-0 text-warning",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_5 = ["d"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("div", { class: "flex flex-col" }, [
  /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-bold text-textPrimary" }, " Aposentada — somente leitura (histórico migrado para a fila de Registro de Plantão) "),
  /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-neutral50" }, " Os registros lançados à mão no painel viraram Registro de Atividade. Aprovar, reprovar e reverter agora acontecem na fila de Registro de Plantão. ")
], -1)
const _hoisted_7 = { class: "flex items-center justify-end" }
const _hoisted_8 = { class: "flex items-center gap-3" }
const _hoisted_9 = {
  key: 2,
  class: "w-full flex flex-col items-center justify-center gap-4 py-16 text-center",
  "data-testid": "check-validation-error-state"
}
const _hoisted_10 = {
  class: "w-10 h-10 text-negative",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_11 = ["d"]
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-textPrimary" }, " Não foi possível carregar os registros aguardando validação ", -1)
const _hoisted_13 = {
  key: 0,
  class: "text-sm text-neutral50",
  "data-testid": "check-validation-error-message"
}

import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { mdiAlertCircleOutline, mdiAlertOutline, mdiClockOutline, mdiTrayArrowDown } from '@mdi/js'

import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { CheckValidationStatus } from '@/Network/Types/Responses/CheckValidationResponses'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  CHECK_VALIDATION_RETIRED_MESSAGE,
  useCheckValidationStore
} from '@/pinia-store/useCheckValidationStore'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import Network from '@/Network'
import { approvalsQueueLocation } from '@/modules/company/Approvals/Hub/queues'
import type { ExportCheckValidationParams } from '@/Network/Types/Requests/CheckValidationRequests'

import CheckValidationFilterBar from './components/CheckValidationFilterBar.vue'
import CheckValidationTable from './components/CheckValidationTable.vue'
import CheckValidationTableSkeleton from './components/CheckValidationTableSkeleton.vue'
import CheckValidationEmptyState from './components/CheckValidationEmptyState.vue'
import EvaluateCheckModal from './components/EvaluateCheckModal.vue'
import type {
  ApproveCheckPayload,
  RejectCheckModalPayload
} from './components/EvaluateCheckModal.vue'
import CheckValidationDetailsModal from './components/CheckValidationDetailsModal.vue'
import ReverseCheckModal from './components/ReverseCheckModal.vue'
import type { ReverseCheckPayload } from './components/ReverseCheckModal.vue'
import ExportEvidencesModal from './components/ExportEvidencesModal.vue'
import type { ExportPayload } from './components/ExportEvidencesModal.vue'
import { isCheckValidationStaleRowError } from './errors'
import { buildCheckValidationFilterTags } from './filterTags'
import type { CheckValidationRow } from './types'


export default /*#__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const checkValidationStore = useCheckValidationStore()
const {
  visibleRows,
  page,
  limit,
  pageCount,
  search,
  statuses,
  periodDays,
  healthUnitIds,
  scheduleIds,
  isLoading,
  isSubmitting,
  hasError,
  loadError,
  hasActiveFilters,
  history,
  isHistoryLoading,
  historyError,
  isRetired
} = storeToRefs(checkValidationStore)

const router = useRouter()

// Q27 — quem aposenta esta fila é o backend (`manualCheckBlockEnabled`), pela
// MESMA verdade que bloqueia a inserção manual de check-in/out. Nenhuma flag de
// front: a tela só reflete o que a API já decidiu.
const dsAttendanceStore = useDsAttendanceStore()

const goToDsAttendanceQueue = () => {
  const location = approvalsQueueLocation('attendance')
  void router.push(location.query ? { path: location.path, query: location.query } : location.path)
}

const healthUnitStore = useHealthUnitStore()
const scheduleStore = useScheduleStore()
const { listHealthUnits } = storeToRefs(healthUnitStore)
const { listSchedules } = storeToRefs(scheduleStore)

const toast = useToast()
const { notifyError } = useErrorToast()

const exportFilterTags = computed(() =>
  buildCheckValidationFilterTags({
    search: search.value,
    statuses: statuses.value,
    onlyOverdue: false,
    periodDays: periodDays.value,
    unitIds: healthUnitIds.value.map(String),
    scheduleIds: scheduleIds.value.map(String),
    unitOptions: listHealthUnits.value.map((u) => ({ label: u.title, value: u.value })),
    scheduleOptions: listSchedules.value.map((s) => ({ label: s.title, value: s.value }))
  })
)

const isRowEvidenceUnavailable = (row: CheckValidationRow) =>
  checkValidationStore.isEvidenceUnavailable(row.checkId)

// ---- Avaliar ---------------------------------------------------------------
const isEvaluateOpen = ref(false)
const evaluateRow = ref<CheckValidationRow | null>(null)
const pendingQueue = ref<CheckValidationRow[]>([])
const pendingQueueIndex = ref(0)

const pendingQueuePosition = computed(() =>
  pendingQueue.value.length > 0 ? pendingQueueIndex.value + 1 : 0
)

const hasPendingRows = computed(() =>
  visibleRows.value.some((r) => r.status === CheckValidationStatus.PENDING_REVIEW)
)

const evaluateFirstPending = () => {
  const pending = visibleRows.value.filter((r) => r.status === CheckValidationStatus.PENDING_REVIEW)
  if (pending.length === 0) return
  pendingQueue.value = pending
  pendingQueueIndex.value = 0
  evaluateRow.value = pending[0]
  isEvaluateOpen.value = true
}

const onEvaluate = (row: CheckValidationRow) => {
  pendingQueue.value = []
  pendingQueueIndex.value = 0
  evaluateRow.value = row
  isEvaluateOpen.value = true
}

// ---- Detalhes --------------------------------------------------------------
const isDetailsOpen = ref(false)
const detailsRow = ref<CheckValidationRow | null>(null)

const onDetails = (row: CheckValidationRow) => {
  detailsRow.value = row
  isDetailsOpen.value = true
  void checkValidationStore.fetchHistory(row.checkId)
}

const onDetailsReverse = (row: CheckValidationRow) => {
  // Q27 — a reversão também é decisão: com a fila aposentada ela nem abre o
  // modal; a explicação vai num toast em vez de um clique que não faz nada.
  if (isRetired.value) {
    toast.info(CHECK_VALIDATION_RETIRED_MESSAGE)
    return
  }
  onReverse(row)
}

function advanceOrClose() {
  if (pendingQueue.value.length === 0) {
    isEvaluateOpen.value = false
    return
  }
  const nextIndex = pendingQueueIndex.value + 1
  if (nextIndex < pendingQueue.value.length) {
    pendingQueueIndex.value = nextIndex
    evaluateRow.value = pendingQueue.value[nextIndex]
  } else {
    pendingQueue.value = []
    isEvaluateOpen.value = false
  }
}

async function runDecision(
  action: () => Promise<void>,
  successMessage: string,
  closeModal: () => void
) {
  try {
    await action()
    toast.success(successMessage)
    closeModal()
    await checkValidationStore.fetchQueue()
  } catch (error) {
    if (isCheckValidationStaleRowError(error)) {
      notifyError(error, 'Este registro foi alterado por outro usuário. A lista foi atualizada.')
      closeEvaluateModal()
      await checkValidationStore.fetchQueue()
      return
    }
    notifyError(error, 'Não foi possível avaliar o registro')
  }
}

const closeEvaluateModal = () => {
  pendingQueue.value = []
  pendingQueueIndex.value = 0
  isEvaluateOpen.value = false
}

const onApprove = ({ checkId, version }: ApproveCheckPayload) =>
  runDecision(
    () => checkValidationStore.approve(checkId, version),
    'Registro aprovado',
    pendingQueue.value.length > 0 ? advanceOrClose : closeEvaluateModal
  )

const onReject = ({ checkId, version, reason, observation }: RejectCheckModalPayload) =>
  runDecision(
    () => checkValidationStore.reject(checkId, { version, reason, observation }),
    'Registro reprovado',
    pendingQueue.value.length > 0 ? advanceOrClose : closeEvaluateModal
  )

// ---- Reverter ---------------------------------------------------------------
const isReverseOpen = ref(false)
const reverseRow = ref<CheckValidationRow | null>(null)

const onReverse = (row: CheckValidationRow) => {
  reverseRow.value = row
  isReverseOpen.value = true
}

const closeReverseAndDetailsModals = () => {
  isReverseOpen.value = false
  isDetailsOpen.value = false
}

const onConfirmReverse = ({ checkId, version, justification }: ReverseCheckPayload) =>
  runDecision(
    () => checkValidationStore.reverse(checkId, version, justification),
    'Aprovação revertida. O registro voltou para análise.',
    closeReverseAndDetailsModals
  )

// ---- Exportar ---------------------------------------------------------------
const isExportOpen = ref(false)
const isExporting = ref(false)

async function onExport(payload: ExportPayload) {
  try {
    isExporting.value = true

    const params: ExportCheckValidationParams = { format: payload.format }

    if (payload.dateType === 'plantao') {
      params.startDate = payload.startDate
      params.endDate = payload.endDate
    } else {
      params.uploadStartDate = payload.startDate
      params.uploadEndDate = payload.endDate
    }

    if (statuses.value.length > 0) params.statuses = [...statuses.value]
    if (scheduleIds.value.length > 0) params.scheduleIds = [...scheduleIds.value]
    if (healthUnitIds.value.length > 0) params.healthUnitIds = [...healthUnitIds.value]

    await Network.checkValidation.exportEvidences(params)
    toast.success('Exportação concluída')
    isExportOpen.value = false
  } catch (error) {
    notifyError(error, 'Não foi possível exportar as evidências')
  } finally {
    isExporting.value = false
  }
}

onMounted(() => {
  void checkValidationStore.fetchQueue()
  // Tolerante a backend antigo: falha volta ao default (fila NÃO aposentada).
  void dsAttendanceStore.fetchRegistrationSettings()
})

onUnmounted(() => {
  checkValidationStore.$reset()
})

return (_ctx: any,_cache: any) => {
  const _component_v_pagination = _resolveComponent("v-pagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isRetired))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            (_openBlock(), _createElementBlock("svg", _hoisted_4, [
              _createElementVNode("path", {
                d: _unref(mdiAlertOutline),
                fill: "currentColor"
              }, null, 8, _hoisted_5)
            ])),
            _hoisted_6
          ]),
          _createVNode(OutlineButton, {
            text: "Ir para Registro de Plantão",
            width: "auto",
            height: "36px",
            "data-testid": "check-validation-goto-ds-attendance",
            onClick: goToDsAttendanceQueue
          })
        ]))
      : _createCommentVNode("", true),
    _createVNode(CheckValidationFilterBar, {
      search: _unref(search),
      statuses: _unref(statuses),
      "period-days": _unref(periodDays),
      "health-unit-ids": _unref(healthUnitIds),
      "schedule-ids": _unref(scheduleIds),
      onSearch: _unref(checkValidationStore).setSearch,
      onFilterStatuses: _unref(checkValidationStore).setStatuses,
      onFilterPeriod: _unref(checkValidationStore).setPeriodDays,
      onFilterUnits: _unref(checkValidationStore).setHealthUnitIds,
      onFilterSchedules: _unref(checkValidationStore).setScheduleIds,
      onClearFilters: _unref(checkValidationStore).clearFilters
    }, null, 8, ["search", "statuses", "period-days", "health-unit-ids", "schedule-ids", "onSearch", "onFilterStatuses", "onFilterPeriod", "onFilterUnits", "onFilterSchedules", "onClearFilters"]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _createVNode(OutlineButton, {
          text: "Exportar evidências",
          width: "auto",
          height: "36px",
          "prepend-icon": _unref(mdiTrayArrowDown),
          "data-testid": "check-validation-export-button",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (isExportOpen.value = true))
        }, null, 8, ["prepend-icon"]),
        _createVNode(FilledButton, {
          text: "Avaliar Pendentes",
          width: "auto",
          height: "36px",
          "prepend-icon": _unref(mdiClockOutline),
          disabled: _unref(isRetired) || !hasPendingRows.value,
          "data-testid": "check-validation-evaluate-pending-button",
          onClick: evaluateFirstPending
        }, null, 8, ["prepend-icon", "disabled"])
      ])
    ]),
    (_unref(isLoading))
      ? (_openBlock(), _createBlock(CheckValidationTableSkeleton, {
          key: 1,
          rows: _unref(limit)
        }, null, 8, ["rows"]))
      : (_unref(hasError))
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            (_openBlock(), _createElementBlock("svg", _hoisted_10, [
              _createElementVNode("path", {
                d: _unref(mdiAlertCircleOutline),
                fill: "currentColor"
              }, null, 8, _hoisted_11)
            ])),
            _hoisted_12,
            (_unref(loadError))
              ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(_unref(loadError)), 1))
              : _createCommentVNode("", true),
            _createVNode(OutlineButton, {
              text: "Tentar novamente",
              width: "200px",
              "data-testid": "check-validation-retry-button",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(checkValidationStore).fetchQueue()))
            })
          ]))
        : (_unref(visibleRows).length === 0)
          ? (_openBlock(), _createBlock(CheckValidationEmptyState, {
              key: 3,
              "has-filters": _unref(hasActiveFilters),
              onClearFilters: _unref(checkValidationStore).clearFilters
            }, null, 8, ["has-filters", "onClearFilters"]))
          : (_openBlock(), _createBlock(CheckValidationTable, {
              key: 4,
              rows: _unref(visibleRows),
              "is-evidence-unavailable": isRowEvidenceUnavailable,
              "is-retired": _unref(isRetired),
              onEvaluate: onEvaluate,
              onDetails: onDetails
            }, null, 8, ["rows", "is-retired"])),
    (!_unref(hasError) && _unref(pageCount) > 1)
      ? (_openBlock(), _createBlock(_component_v_pagination, {
          key: 5,
          "model-value": _unref(page),
          "total-visible": 7,
          length: _unref(pageCount),
          disabled: _unref(isLoading),
          density: "comfortable",
          color: "primary",
          "data-testid": "check-validation-pagination",
          "onUpdate:modelValue": _unref(checkValidationStore).setPage
        }, null, 8, ["model-value", "length", "disabled", "onUpdate:modelValue"]))
      : _createCommentVNode("", true),
    _createVNode(EvaluateCheckModal, {
      modelValue: isEvaluateOpen.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isEvaluateOpen).value = $event)),
      row: evaluateRow.value,
      saving: _unref(isSubmitting),
      "queue-position": pendingQueuePosition.value,
      "queue-total": pendingQueue.value.length,
      onApprove: onApprove,
      onReject: onReject,
      onEvidenceUnavailable: _unref(checkValidationStore).markEvidenceUnavailable,
      onEvidenceAvailable: _unref(checkValidationStore).clearEvidenceUnavailable
    }, null, 8, ["modelValue", "row", "saving", "queue-position", "queue-total", "onEvidenceUnavailable", "onEvidenceAvailable"]),
    _createVNode(CheckValidationDetailsModal, {
      modelValue: isDetailsOpen.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isDetailsOpen).value = $event)),
      row: detailsRow.value,
      history: _unref(history),
      "is-history-loading": _unref(isHistoryLoading),
      "history-error": _unref(historyError),
      onReverse: onDetailsReverse
    }, null, 8, ["modelValue", "row", "history", "is-history-loading", "history-error"]),
    _createVNode(ReverseCheckModal, {
      modelValue: isReverseOpen.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((isReverseOpen).value = $event)),
      row: reverseRow.value,
      saving: _unref(isSubmitting),
      onReverse: onConfirmReverse
    }, null, 8, ["modelValue", "row", "saving"]),
    _createVNode(ExportEvidencesModal, {
      modelValue: isExportOpen.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((isExportOpen).value = $event)),
      exporting: isExporting.value,
      "filter-tags": exportFilterTags.value,
      onExport: onExport
    }, null, 8, ["modelValue", "exporting", "filter-tags"])
  ]))
}
}

})